// DEPENDENCIES ---------------------------------------------------------------- //

// import React from 'react';

import { css } from '@emotion/css';
import { Theme, Typography, useTheme } from '@mui/material';

import { useTimeline } from '@components/Timeline/Timeline/TimelineProvider';

// TYPES ---------------------------------------------------------------- //

const hourTimes = [
  `12 AM`,
  `1 AM`,
  `2 AM`,
  `3 AM`,
  `4 AM`,
  `5 AM`,
  `6 AM`,
  `7 AM`,
  `8 AM`,
  `9 AM`,
  `10 AM`,
  `11 AM`,
  `12 PM`,
  `1 PM`,
  `2 PM`,
  `3 PM`,
  `4 PM`,
  `5 PM`,
  `6 PM`,
  `7 PM`,
  `8 PM`,
  `9 PM`,
  `10 PM`,
  `11 PM`,
  `12 AM`,
];
const quarterAfterTimes = [
  `12:15 AM`,
  `1:15 AM`,
  `2:15 AM`,
  `3:15 AM`,
  `4:15 AM`,
  `5:15 AM`,
  `6:15 AM`,
  `7:15 AM`,
  `8:15 AM`,
  `9:15 AM`,
  `10:15 AM`,
  `11:15 AM`,
  `12:15 PM`,
  `1:15 PM`,
  `2:15 PM`,
  `3:15 PM`,
  `4:15 PM`,
  `5:15 PM`,
  `6:15 PM`,
  `7:15 PM`,
  `8:15 PM`,
  `9:15 PM`,
  `10:15 PM`,
  `11:15 PM`,
];
const halfHourTimes = [
  `12:30 AM`,
  `1:30 AM`,
  `2:30 AM`,
  `3:30 AM`,
  `4:30 AM`,
  `5:30 AM`,
  `6:30 AM`,
  `7:30 AM`,
  `8:30 AM`,
  `9:30 AM`,
  `10:30 AM`,
  `11:30 AM`,
  `12:30 PM`,
  `1:30 PM`,
  `2:30 PM`,
  `3:30 PM`,
  `4:30 PM`,
  `5:30 PM`,
  `6:30 PM`,
  `7:30 PM`,
  `8:30 PM`,
  `9:30 PM`,
  `10:30 PM`,
  `11:30 PM`,
];
const quarterBeforeTimes = [
  `12:45 AM`,
  `1:45 AM`,
  `2:45 AM`,
  `3:45 AM`,
  `4:45 AM`,
  `5:45 AM`,
  `6:45 AM`,
  `7:45 AM`,
  `8:45 AM`,
  `9:45 AM`,
  `10:45 AM`,
  `11:45 AM`,
  `12:45 PM`,
  `1:45 PM`,
  `2:45 PM`,
  `3:45 PM`,
  `4:45 PM`,
  `5:45 PM`,
  `6:45 PM`,
  `7:45 PM`,
  `8:45 PM`,
  `9:45 PM`,
  `10:45 PM`,
  `11:45 PM`,
];

// NOTE: This is how the margin is calculated for the time ticks (takes into account the tick multiplier and scale)
// marginRight: 5 * zoom - 1, // minutes * constant mult * scale - 1px for line width

interface HourFullProps {
  index: number;
  hourTime: string;
  zoom: number;
}

interface HourHalfProps {
  index: number;
  hourTime: string;
  halfHourTime: string;
  zoom: number;
}

interface HourQuarterProps {
  index: number;
  hourTime: string;
  quarterAfterTime: string;
  halfHourTime: string;
  quarterBeforeTime: string;
  zoom: number;
}

// COMPONENTS ---------------------------------------------------------------- //

const HourFull = ({ index, hourTime, zoom }: HourFullProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  if (hourTime)
    return (
      <div className={cls.hour}>
        <div className={cls.hourContainer}>
          {index !== hourTimes.length - 1 ? (
            <>
              <div>
                <div className={cls.tickFull} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
              <div>
                <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
              <div>
                <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
              <div>
                <div className={cls.tickQuarter} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
              <div>
                <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
              <div>
                <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
              <div>
                <div className={cls.tickHalf} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
              <div>
                <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
              <div>
                <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
              <div>
                <div className={cls.tickQuarter} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
              <div>
                <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
              <div>
                <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
              </div>
            </>
          ) : (
            <div>
              <div className={cls.tickFinal} />
            </div>
          )}
        </div>

        <div className={index === 0 ? cls.labelStart : index === hourTimes.length - 1 ? cls.labelEnd : cls.label}>
          <Typography sx={clsx.labelTxtFull}>{hourTime}</Typography>
        </div>
      </div>
    );
  return null;
};

const HourHalf = ({ index, hourTime, halfHourTime, zoom }: HourHalfProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  return (
    <>
      {hourTime ? (
        <div className={cls.hour}>
          <div className={cls.hourContainer}>
            {index !== hourTimes.length - 1 ? (
              <>
                <div>
                  <div className={cls.tickFull} style={{ marginRight: 5 * zoom - 1 }} />
                </div>
                <div>
                  <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
                </div>
                <div>
                  <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
                </div>
                <div>
                  <div className={cls.tickQuarter} style={{ marginRight: 5 * zoom - 1 }} />
                </div>
                <div>
                  <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
                </div>
                <div>
                  <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
                </div>
              </>
            ) : (
              <div>
                <div className={cls.tickFinal} />
              </div>
            )}
          </div>

          <div className={index === 0 ? cls.labelStart : index === hourTimes.length - 1 ? cls.labelEnd : cls.label}>
            <Typography sx={clsx.labelTxtFull}>{hourTime}</Typography>
          </div>
        </div>
      ) : null}

      {halfHourTime ? (
        <div className={cls.hour}>
          <div className={cls.hourContainer}>
            <div>
              <div className={cls.tickHalf} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
            <div>
              <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
            <div>
              <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
            <div>
              <div className={cls.tickQuarter} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
            <div>
              <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
            <div>
              <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
          </div>

          <div className={cls.label}>
            <Typography sx={clsx.labelTxtHalf}>{halfHourTime}</Typography>
          </div>
        </div>
      ) : null}
    </>
  );
};

const HourQuarter = ({
  index,
  hourTime,
  quarterAfterTime,
  halfHourTime,
  quarterBeforeTime,

  zoom,
}: HourQuarterProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  return (
    <>
      {hourTime ? (
        <div className={cls.hour}>
          <div className={cls.hourContainer}>
            {index !== hourTimes.length - 1 ? (
              <>
                <div>
                  <div className={cls.tickFull} style={{ marginRight: 5 * zoom - 1 }} />
                </div>
                <div>
                  <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
                </div>
                <div>
                  <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
                </div>
              </>
            ) : (
              <div>
                <div className={cls.tickFinal} />
              </div>
            )}
          </div>

          <div className={index === 0 ? cls.labelStart : index === hourTimes.length - 1 ? cls.labelEnd : cls.label}>
            <Typography sx={clsx.labelTxtFull}>{hourTime}</Typography>
          </div>
        </div>
      ) : null}

      {quarterAfterTime ? (
        <div className={cls.hour}>
          <div className={cls.hourContainer}>
            <div>
              <div className={cls.tickQuarter} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
            <div>
              <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
            <div>
              <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
          </div>

          <div className={cls.label}>
            <Typography sx={clsx.labelTxtQuarter}>{quarterAfterTime}</Typography>
          </div>
        </div>
      ) : null}

      {halfHourTime ? (
        <div className={cls.hour}>
          <div className={cls.hourContainer}>
            <div>
              <div className={cls.tickHalf} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
            <div>
              <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
            <div>
              <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
          </div>

          <div className={cls.label}>
            <Typography sx={clsx.labelTxtHalf}>{halfHourTime}</Typography>
          </div>
        </div>
      ) : null}

      {quarterBeforeTime ? (
        <div className={cls.hour}>
          <div className={cls.hourContainer}>
            <div>
              <div className={cls.tickQuarter} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
            <div>
              <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
            <div>
              <div className={cls.tickTwelth} style={{ marginRight: 5 * zoom - 1 }} />
            </div>
          </div>

          <div className={cls.label}>
            <Typography sx={clsx.labelTxtQuarter}>{quarterBeforeTime}</Typography>
          </div>
        </div>
      ) : null}
    </>
  );
};

const LabelLoop = () => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { zoom } = useTimeline();

  let labels: React.ReactNode[] = [];
  for (let i = 0; i < hourTimes.length; i++) {
    const hourTime = hourTimes[i];
    const quarterAfterTime = quarterAfterTimes[i] || ``;
    const halfHourTime = halfHourTimes[i] || ``;
    const quarterBeforeTime = quarterBeforeTimes[i] || ``;

    if (zoom >= 8) {
      labels.push(
        <HourQuarter
          key={`timeline-head-label-${i}`}
          index={i}
          hourTime={hourTime}
          quarterAfterTime={quarterAfterTime}
          halfHourTime={halfHourTime}
          quarterBeforeTime={quarterBeforeTime}
          zoom={zoom}
        />
      );
    } else if (zoom >= 4) {
      labels.push(
        <HourHalf
          key={`timeline-head-label-${i}`}
          index={i}
          hourTime={hourTime}
          halfHourTime={halfHourTime}
          zoom={zoom}
        />
      );
    } else {
      labels.push(<HourFull key={`timeline-head-label-${i}`} index={i} hourTime={hourTime} zoom={zoom} />);
    }
  }

  return <div className={cls.hourContainer}>{labels.map(label => label)}</div>;
};

// STYLES --------------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  hour: css`
    position: relative;
  `,
  hourContainer: css`
    display: flex;
    align-items: flex-start;
    flex-wrap: nowrap;
  `,

  tickFull: css`
    vertical-align: top;
    width: 0;
    height: 24px;
    border-left: 1px solid ${theme.palette.divider};
    user-select: none;
  `,
  tickHalf: css`
    vertical-align: top;
    width: 0;
    height: 18px;
    border-left: 1px solid ${theme.palette.divider};
    user-select: none;
  `,
  tickQuarter: css`
    vertical-align: top;
    width: 0;
    height: 12px;
    border-left: 1px solid ${theme.palette.divider};
    user-select: none;
  `,
  tickTwelth: css`
    vertical-align: top;
    width: 0;
    height: 6px;
    border-left: 1px solid ${theme.palette.divider};
    user-select: none;
  `,
  tickFinal: css`
    vertical-align: top;
    width: 0;
    height: 24px;
    user-select: none;
  `,

  labelStart: css`
    position: absolute;
    left: 0;
  `,
  label: css`
    position: absolute;
    left: 0;
    transform: translateX(-50%);
  `,
  labelEnd: css`
    position: absolute;
    left: 0;
    transform: translateX(-100%);
  `,
});

const useSxStyles = (theme: Theme) => ({
  labelTxtFull: {
    whiteSpace: 'nowrap',
    marginTop: theme.spacing(0.75),
    lineHeight: 1,
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.disabled,
    userSelect: 'none',
  },
  labelTxtHalf: {
    whiteSpace: 'nowrap',
    marginTop: theme.spacing(1.5),
    lineHeight: 1,
    fontSize: 12,
    fontWeight: 500,
    color: `${theme.palette.text.primary}48`,
    userSelect: 'none',
  },
  labelTxtQuarter: {
    whiteSpace: 'nowrap',
    marginTop: theme.spacing(2.25),
    lineHeight: 1,
    fontSize: 12,
    fontWeight: 500,
    color: `${theme.palette.text.primary}36`,
    userSelect: 'none',
  },
});

// -------------------------------- EXPORT -------------------------------- //

export default LabelLoop;
